import { actions, dom, events } from '@omegagrid/core';
import rp, { GridEditor, Application, ParsedComponentId, EditorContainer, formatAccessRole, formatUser, formatAccounting, formatAccountingGroup } from '@reporting/rp-app-host';
import { UserAccessGridEditorAdapter, UserAccessGridOptions, EntityAccessGridEditorAdapter, EntityUsersGridEditorAdapter } from './adapters';
import { Accounting, User, AccountingGroup } from "@reporting/rp-types/core";
import { TreeAdapter, TreeAdapterParams, TreeOptions, TreeSource } from '@omegagrid/tree';
import { msg } from '@omegagrid/localize';
import { GroupTreeAdapter } from "@reporting/rp-mf-accounting"
import { AccessRole, EntityAccessType } from '@reporting/rp-types/users';
import { ToolbarDropdown } from '@omegagrid/toolbar';

type accountingUserGroupTreeAdapterOptions = {
	all?: boolean,
	accountings?: boolean,
	roles?: boolean,
	users?: boolean,
};

const createAccountingUserGroupTreeAdapter = (g?: number | null, options?: accountingUserGroupTreeAdapterOptions | string) => new class extends TreeAdapter {

	async getTree(params?: TreeAdapterParams): Promise<TreeSource> {
		const parentKey = typeof options === 'string' ? options : params?.parentKey || null;
		switch (parentKey) {
			case 'accountings':
				return (await rp.ms('accounting').get<Accounting[]>(
					`tree${g ? `/${g}` : ''}`,
					{ fields: ['accounting', 'company'] })
				).map(a => ({k: `a-${a.accounting}`, v: formatAccounting(a), l: a.level + 1, icon: 'building'}));
			case 'roles':
				return (await rp.ms('users').get<AccessRole[]>(
					'accessRoles',
					{ fields: ['id_access_role', 'role'], ...(g ? { g } : {}) })
				).map(role => ({k: `r-${role.id_access_role}`, v: formatAccessRole(role), l: 1, icon: 'users'}));
			case 'users':
				return (await rp.ms('users').get<User[]>(
					g ? `allUsers/${g}` : 'groupUsers',
					{ fields: ['user_name', 'login_name'] }
				)).map(user => ({k: `u-${user.user_name}`, v: formatUser(user), l: 1, icon: 'user'}));
			default:
				return this.getDefaultItems();
		}
	}

	getDefaultItems(): TreeSource {
		const items: TreeSource = [];
		const opts: accountingUserGroupTreeAdapterOptions = typeof options === 'string' ? null : options;
		if (!opts || opts.all !== false) items.push({k: '0', v: `<${msg('all')}>`});
		if (!opts || opts.accountings !== false) items.push({collapsed: true, ch: false, count: 1, icon: 'building', k: 'accountings', v: `${msg('Companies')}`});
		if (!opts || opts.roles !== false) items.push({collapsed: true, ch: false, count: 1, icon: 'users', k: 'roles', v: `${msg('User groups')}`});
		if (!opts || opts.users !== false) items.push({collapsed: true, ch: false, count: 1, icon: 'user', k: 'users', v: `${msg('Users')}`});
		return items;
	}

	getOptions(): TreeOptions {
		return {
			selectMode: 'single',
			search: true,
			allowEmpty: true,
		};
	}
};

export const createUserAccessGridEditorContainer = async (app: Application, componentId: ParsedComponentId) => {
	const containerParams: UserAccessGridOptions = {};
	for (let i = 1; i + 1 < componentId.ids.length; i += 2) {
		containerParams[componentId.ids[i] as keyof UserAccessGridOptions] = componentId.ids[i + 1];
	}

	let accounting: Accounting | null = null;
	let user: User | null = null;
	let role: AccessRole | null = null;

	if (containerParams.u) {
		user = await rp.ms('users').get<User>(containerParams.u);
	} else if (containerParams.r) {
		role = await rp.ms('users').get<AccessRole>(`accessRoles/${containerParams.r}`);
	} else if (containerParams.a) {
		accounting = await rp.ms('accounting').get<Accounting>(containerParams.a);
	}

	const container = dom.createElement<EditorContainer>('rp-editor-container', {
		items: [{
			id: 'object',
			width: 400,
			value:
				user ? [[user.user_name, 0, 1, {v: formatUser(user)}]] :
				role ? [[role.id_access_role, 0, 1, {v: formatAccessRole(role)}]] :
				accounting ? [[accounting.accounting, 0, 1, {v: formatAccounting(accounting)}]] : [],
			type: 'dropdown',
			caption: `${msg('Company')} / ${msg('Group')} / ${msg('User')}`,
			adapter: createAccountingUserGroupTreeAdapter(parseInt(containerParams.g)),
		}],
		createComponent: id => rp.host.createComponent(id),
		// triggered when toolbar dropdown values change
		updateComponentId: (values) => {
			let id = values['object'] && values['object'] !== '0' ? `-${values['object']}` : '';
			if (id === '') {
				if (containerParams.g) id += `-g-${containerParams.g}`;
				if (containerParams.a) id += `-a-${containerParams.a}`;
				if (containerParams.r) id += `-r-${containerParams.r}`;
				if (containerParams.u) id += `-u-${containerParams.u}`;
			}
			return `users-userAccess${id}`;
		}
	});

	container.addEventListener('change', (e: events.ChangeEvent) => {
		const key = e.value['object'];
		let title = msg('Access');
		const dropdown = container.toolbar.controls.get('object') as ToolbarDropdown;
		const node = dropdown.tree.tree.model.nodeMap.get(key);
		if (node?.value) title += `: ${node.value}`;

		if (key) {
			rp.host.updateComponent(container.componentId, { id: `users-userAccessContainer-${key}`, title });
		}
	});

	return container;
}

export const createUserAccessSuperGridEditorContainer = async (app: Application, componentId: ParsedComponentId) => {
	const g = componentId.ids[1] ? componentId.ids[1] : null;
	const group: AccountingGroup | null = g ? await rp.ms('accounting').get<AccountingGroup>(`groups/${g}`) : null;

	const container = dom.createElement<EditorContainer>('rp-editor-container', {
		items: [{
			id: 'group',
			width: 250,
			value: group ? [[group.id_group, 0, 1, {v: formatAccountingGroup(group)}]] : [],
			type: 'dropdown',
			caption: msg('Group'),
			adapter: new GroupTreeAdapter({ selectMode: 'single', autoClose: true, checkboxes: false, search: true }),
		}],
		createComponent: id => rp.host.createComponent(id),
		// triggered when toolbar dropdown values change
		updateComponentId: (values) => {
			const key = values['group'] ?? g;
			if (!key) return `users-alert-Select group`;
			return `users-userAccessContainer-g-${key}`;
		}
	});

	container.addEventListener('change', (e: events.ChangeEvent) => {
		const key = e.value['group'];
		if (!key) return;
		rp.host.updateComponent(
			container.componentId,
			{ id: `users-userAccessSuperContainer${key}`, title: msg('Access') }
		);
	});

	return container;
}

export const createUserAccessGridEditor = async (app: Application, componentId: ParsedComponentId) => {
	const containerParams: UserAccessGridOptions = {};
	for (let i = 1; i + 1 < componentId.ids.length; i += 2) {
		containerParams[componentId.ids[i] as keyof UserAccessGridOptions] = componentId.ids[i + 1];
	}

	const editor = dom.createElement<GridEditor>('rp-editor-grid', {
		adapter: new UserAccessGridEditorAdapter(containerParams)
	});

	editor.addEventListener('editor.action', (e: actions.ActionEvent) => {
		const value: any = e.action.value;
		if (value.type == 'role') {
			rp.host.open(`users-userAccessContainer-r-${value.id}`, {title: value.value});
		} else if (value.type == 'user') {
			rp.host.open(`users-userAccessContainer-u-${value.id}`, {title: value.value});
		}
	});

	return editor;
}

export const createEntityAccessGridEditorContainer = async (app: Application, componentId: ParsedComponentId) => {
	const a = componentId.ids[1] === 'a' ? componentId.ids[2] : null;
	const accounting = a ? await rp.ms('accounting').get<Accounting>(a) : rp.accountingProvider.accounting;

	const container = dom.createElement<EditorContainer>('rp-editor-container', {
		items: [{
			id: 'accounting',
			width: 300,
			value: [[accounting.accounting, 0, 1, { v: formatAccounting(accounting) }]],
			type: 'dropdown',
			caption: msg('Company'),
			adapter: createAccountingUserGroupTreeAdapter(null, 'accountings'),
		}],
		createComponent: id => rp.host.createComponent(id),
		// triggered when toolbar dropdown values change
		updateComponentId: (values) => {
			const id = values['accounting'] ?? `a-${accounting.accounting}`;
			return `users-entityAccess-${id}`;
		}
	});

	container.addEventListener('change', (e: events.ChangeEvent) => {
		const key = e.value['accounting'] as string;
		if (!key) return;
		rp.host.updateComponent(
			container.componentId,
			{ id: `users-entityAccessContainer-${key}`, title: `${msg('Access')} - ${msg('Objects')} [${key.substring(2)}]` }
		);
	});

	return container;
}

export const createEntityAccessGridEditor = (app: Application, componentId: ParsedComponentId) => {
	const a = componentId.ids[1] === 'a' ? parseInt(componentId.ids[2]) : rp.accountingProvider.a;
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	const adapter = new EntityAccessGridEditorAdapter(a);
	component.adapter = adapter;
	return component;
}

export const createEntityUsersGridEditor = (app: Application, componentId: ParsedComponentId) => {
	const type = componentId.ids[1] as EntityAccessType;
	const id = componentId.ids[2];
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	const adapter = new EntityUsersGridEditorAdapter(type, id);
	component.adapter = adapter;
	return component;
}
